import {rgbToHex} from 'views/home/bannerColors';

/**
 * Matches the API: at least the home page's 200x113 image box, so the image
 * fills it without stretching, and at most 10 MB. Twice the box looks sharpest.
 */
export const MIN_IMAGE_SIZE = {width: 200, height: 113};
export const SHARPEST_IMAGE_SIZE = {width: 400, height: 226};
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES =
  'image/png,image/jpeg,image/gif,image/webp,image/avif,image/bmp';

const SAMPLE_SIZE = 64;
const MIN_DISTANCE = 60;

type Bucket = {r: number; g: number; b: number; count: number};

const saturation = (r: number, g: number, b: number) => {
  const max = Math.max(r, g, b);
  return max === 0 ? 0 : (max - Math.min(r, g, b)) / max;
};

/**
 * The distinct colors in RGBA pixel data, most prominent first. Pixels are
 * grouped at 5 bits per channel, and vivid groups count extra so a splash of
 * brand color can outrank a large neutral backdrop.
 */
export const paletteFromPixels = (data: Uint8ClampedArray, maxColors = 6): string[] => {
  const buckets = new Map<number, Bucket>();
  for (let i = 0; i + 3 < data.length; i += 4) {
    const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
    if (a < 128) {
      continue;
    }
    const key = ((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3);
    const bucket = buckets.get(key) ?? {r: 0, g: 0, b: 0, count: 0};
    bucket.r += r;
    bucket.g += g;
    bucket.b += b;
    bucket.count += 1;
    buckets.set(key, bucket);
  }

  const ranked = [...buckets.values()]
    .map(({r, g, b, count}) => {
      const [mr, mg, mb] = [r / count, g / count, b / count];
      return {r: mr, g: mg, b: mb, score: count * (1 + 2 * saturation(mr, mg, mb))};
    })
    .sort((a, b) => b.score - a.score);

  const picked: typeof ranked = [];
  for (const color of ranked) {
    if (
      picked.every(
        (other) =>
          Math.hypot(color.r - other.r, color.g - other.g, color.b - other.b) >= MIN_DISTANCE
      )
    ) {
      picked.push(color);
    }
    if (picked.length === maxColors) {
      break;
    }
  }
  return picked.map(({r, g, b}) => rgbToHex([r, g, b]));
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the image'));
    img.src = src;
  });

/** Width and height as the browser shows the image, after any rotation in its metadata. */
export const readImageSize = async (src: string) => {
  const img = await loadImage(src);
  return {width: img.naturalWidth, height: img.naturalHeight};
};

/** The image's palette. Use a blob URL so the canvas can read the pixels. */
export const extractPalette = async (src: string, maxColors = 6) => {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_SIZE;
  canvas.height = SAMPLE_SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    return [];
  }
  context.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
  return paletteFromPixels(context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data, maxColors);
};

/** A problem with a picked file that would make the API reject it, or null. */
export const imageFileProblem = async (file: File, previewUrl: string) => {
  if (!ACCEPTED_IMAGE_TYPES.split(',').includes(file.type)) {
    return 'Choose a PNG, JPEG, GIF, WebP, AVIF, or BMP image.';
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return 'Images can be up to 10 MB.';
  }
  try {
    const {width, height} = await readImageSize(previewUrl);
    if (width < MIN_IMAGE_SIZE.width || height < MIN_IMAGE_SIZE.height) {
      return `This image is ${width}×${height}. Use one at least ${MIN_IMAGE_SIZE.width}×${MIN_IMAGE_SIZE.height} so it fills the image card.`;
    }
  } catch {
    return "This image can't be read. Try another file.";
  }
  return null;
};
